import React from 'react';
import PropTypes from 'prop-types';
import { Classes, Icon } from '@blueprintjs/core';
import { MENU_SHORTCUT_LABEL, itemShortcutLabel } from 'helpers/shortcuts';

// Panel behind the top bar's "New" button: a quick-create palette. Type to
// filter, ↑/↓ to move, Enter to create. Each item has a one-letter `shortcut`:
// typing just that letter puts it first, so "J" then Enter starts a job, and
// Alt/⌥ + the letter opens it from anywhere (helpers/shortcuts). Each
// item's icon takes the colour of the panel it opens (`tone`, as FormDrawer's:
// jobs, field, estimates, billing, customers).
// Styles: styles/search-filters-bar.scss (.create-menu).

const matches = (item, query) => {
  if (!query) return 0;
  const q = query.toLowerCase();
  const label = item.label.toLowerCase();
  if (q.length === 1 && item.shortcut && item.shortcut.toLowerCase() === q) return 3;
  if (label.startsWith(q)) return 2;
  if (label.includes(q) || (item.hint || '').toLowerCase().includes(q)) return 1;
  return -1;
};

class CreateMenu extends React.Component {
  state = { query: '', active: 0 };

  input = React.createRef();
  buttons = {};

  componentDidMount() {
    // After the popover has opened and taken focus itself: the search box, or
    // (when there's no search, e.g. a technician's one item) the first item.
    this.focusTimer = setTimeout(() => {
      if (this.input.current) {
        this.input.current.focus();
      } else {
        const items = this.visibleGroups.flatMap((group) => group.items);
        if (items[0]) this.focusItem(items[0]);
      }
    }, 30);
  }

  componentWillUnmount() {
    clearTimeout(this.focusTimer);
  }

  // Groups with only the items matching the search, best match first when searching.
  get visibleGroups() {
    const { query } = this.state;
    const groups = this.props.groups.filter((group) => group.items.length > 0);
    if (!query.trim()) return groups;
    const ranked = groups
      .flatMap((group) => group.items.map((item) => ({ item, score: matches(item, query.trim()) })))
      .filter(({ score }) => score >= 0)
      .sort((a, b) => b.score - a.score)
      .map(({ item }) => item);
    return ranked.length ? [{ title: 'Results', items: ranked }] : [];
  }

  focusItem(item) {
    const button = this.buttons[item.label];
    if (button) button.focus();
  }

  // Arrows move the highlight and keep it in view (the list scrolls on short
  // screens). Without a search box, focus moves with it.
  moveTo(next, items) {
    this.setState({ active: next }, () => {
      const item = items[next];
      const button = item && this.buttons[item.label];
      if (!button) return;
      if (this.input.current) {
        if (button.scrollIntoView) button.scrollIntoView({ block: 'nearest' });
      } else {
        button.focus();
      }
    });
  }

  onKeyDown = (event, items) => {
    const { active } = this.state;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.moveTo(Math.min(active + 1, items.length - 1), items);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveTo(Math.max(active - 1, 0), items);
    } else if (event.key === 'Enter' && items[active]) {
      event.preventDefault();
      // Clicking the item's own button runs it and closes the popover.
      const button = this.buttons[items[active].label];
      if (button) button.click();
    }
  };

  render() {
    const { query, active } = this.state;
    const groups = this.visibleGroups;
    const items = groups.flatMap((group) => group.items);
    const searching = Boolean(query.trim());
    const showSearch = this.props.groups.reduce((n, group) => n + group.items.length, 0) > 3;
    let index = -1;

    return (
      <div className="create-menu" onKeyDown={(event) => this.onKeyDown(event, items)}>
        {showSearch && (
          <div className="create-menu__search">
            <Icon icon="search" iconSize={16} />
            <input
              ref={this.input}
              type="text"
              value={query}
              placeholder="Create a job, invoice, client…"
              aria-label="Find something to create"
              aria-controls="create-menu-list"
              aria-activedescendant={items[active] ? `create-menu-${items[active].label}` : undefined}
              onChange={(event) => this.setState({ query: event.target.value, active: 0 })}
            />
            <kbd className="create-menu__kbd">esc</kbd>
          </div>
        )}

        <div className="create-menu__list" id="create-menu-list" role="menu" aria-label="Create">
          {groups.length === 0 && (
            <p className="create-menu__empty">Nothing to create matches “{query.trim()}”.</p>
          )}
          {groups.map((group) => (
            <div key={group.title} className="create-menu__group" role="group" aria-label={group.title}>
              {!searching && groups.length > 1 && (
                <div className="create-menu__group-title" aria-hidden="true">{group.title}</div>
              )}
              {group.items.map((item) => {
                index += 1;
                const position = index;
                const isActive = position === active;
                return (
                  <button
                    key={item.label}
                    id={`create-menu-${item.label}`}
                    ref={(node) => { this.buttons[item.label] = node; }}
                    type="button"
                    role="menuitem"
                    tabIndex={showSearch ? -1 : 0}
                    onFocus={() => !isActive && this.setState({ active: position })}
                    className={`create-menu__item create-menu--${item.tone || 'customers'}${isActive ? ' is-active' : ''} ${Classes.POPOVER_DISMISS}`}
                    onMouseMove={() => !isActive && this.setState({ active: position })}
                    onClick={item.onClick}
                    title={item.shortcut ? `${item.label} (${itemShortcutLabel(item.shortcut)})` : undefined}
                  >
                    <span className="create-menu__icon" aria-hidden="true">
                      <Icon icon={item.icon} iconSize={14} />
                    </span>
                    <span className="create-menu__text">
                      <span className="create-menu__label">{item.label}</span>
                      {item.hint && <span className="create-menu__hint">{item.hint}</span>}
                    </span>
                    {item.shortcut && <kbd className="create-menu__kbd">{itemShortcutLabel(item.shortcut)}</kbd>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {showSearch && (
          <div className="create-menu__footer" aria-hidden="true">
            <span><kbd className="create-menu__kbd">↑</kbd><kbd className="create-menu__kbd">↓</kbd> to move</span>
            <span><kbd className="create-menu__kbd">↵</kbd> to create</span>
            <span>{MENU_SHORTCUT_LABEL} opens this menu</span>
          </div>
        )}
      </div>
    );
  }
}

const itemShape = PropTypes.shape({
  label: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  tone: PropTypes.oneOf(['jobs', 'field', 'estimates', 'billing', 'customers']),
  hint: PropTypes.string,
  shortcut: PropTypes.string,
});

CreateMenu.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(itemShape).isRequired,
    })
  ).isRequired,
};

export default CreateMenu;
